import { Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Layers } from 'lucide-react';
import {
  findColocationUConfig,
  formatUPosition,
  rackU,
  totalU,
  type ColocationConfigOption,
  type ColocationAddon,
  type RackEntry,
} from './colocation-rack-helpers';

/* ── Shared style tokens ─────────────────────────────────────────────── */

const rackCard: React.CSSProperties = {
  border: '1px solid var(--vn-border-subtle)',
  borderRadius: 'var(--vn-radius-md)',
  overflow: 'hidden',
};

const rackCardHeader: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 'var(--vn-space-2) var(--vn-space-3)',
  background: 'var(--vn-bg-subtle)',
  borderBottom: '1px solid var(--vn-border-subtle)',
};

const rackLabel: React.CSSProperties = {
  fontSize: 'var(--vn-font-size-xs)',
  color: 'var(--vn-fg-muted)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
};

const rackUBadge = (matched: boolean): React.CSSProperties => ({
  fontSize: 'var(--vn-font-size-xs)',
  fontWeight: 'var(--vn-font-weight-semibold)',
  color: matched ? 'var(--vn-success-text)' : 'var(--vn-danger-text)',
});

/* ── DataTable column definitions for rack entries ───────────────────── */

/** Indexed rack entry — adds a stable 1-based index for the # column. */
interface IndexedRackEntry extends RackEntry {
  _index: number;
}

const rackColumns: ColumnDef<IndexedRackEntry, unknown>[] = [
  {
    id: 'index',
    header: '#',
    size: 32,
    enableSorting: false,
    cell: ({ row }) => (
      <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)' }}>
        #{row.original._index + 1}
      </span>
    ),
  },
  {
    id: 'idc',
    header: 'IDC',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.idc || <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>,
  },
  {
    id: 'rack',
    header: 'Rack',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.rack || <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>,
  },
  {
    id: 'position',
    header: 'U position',
    enableSorting: false,
    cell: ({ row }) => (
      <span style={{ fontFamily: 'monospace' }}>
        {formatUPosition(row.original.position)}
      </span>
    ),
  },
  {
    id: 'ucount',
    enableSorting: false,
    header: () => (
      <span style={{ display: 'block', textAlign: 'right' }}>U count</span>
    ),
    cell: ({ row }) => {
      const uCount = rackU(row.original);
      return (
        <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {uCount > 0 ? `${uCount} U` : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>}
        </span>
      );
    },
  },
];

/* ── Single config-option rack table ────────────────────────────────── */

interface RackTableProps {
  configoption: ColocationConfigOption;
  label: string;
}

function RackTable({ configoption, label }: RackTableProps) {
  const commitment = configoption.commitment_value ?? 0;
  const racks: RackEntry[] = (configoption.metadata?.racks ?? []).filter(
    (r) => r.idc || r.rack || r.position,
  );
  const used = totalU(racks);
  const matched = used === commitment;
  const indexedRacks: IndexedRackEntry[] = racks.map((r, i) => ({ ...r, _index: i }));

  return (
    <div style={rackCard}>
      <div style={rackCardHeader}>
        <span style={rackLabel}>{label}</span>
        <span style={rackUBadge(matched)}>
          {used} / {commitment} U
        </span>
      </div>
      <DataTable
        columns={rackColumns}
        data={indexedRacks}
        enableSorting={false}
        emptyState={
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            No rack assignments recorded yet
          </span>
        }
      />
    </div>
  );
}

/* ── Public component ────────────────────────────────────────────────── */

export interface ColocationRackGridProps {
  /** Main service configoptions (from `useServiceConfigoptions`). */
  configoptions: ColocationConfigOption[];
  /** Addon services with their own configoptions (from `useServiceAddonConfigoptions`). */
  addons: ColocationAddon[];
}

/**
 * Colocation rack-U assignment grid.
 *
 * Renders one rack table per colocation-u / u configoption found in the main
 * service and each addon. Mirrors the visual intent of legacy Main/AddonServiceColocation
 * (read-only view only; editing is out of scope for @workspace). Returns null when
 * no colocation-u configoption is found.
 */
export function ColocationRackGrid({ configoptions, addons }: ColocationRackGridProps) {
  const mainConfig = findColocationUConfig(configoptions);
  const addonConfigs = addons
    .map((a) => ({
      addon: a,
      config: findColocationUConfig(a.configoptions ?? []),
    }))
    .filter((x): x is { addon: ColocationAddon; config: ColocationConfigOption } => !!x.config);

  if (!mainConfig && addonConfigs.length === 0) return null;

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Rack assignment</CardTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {mainConfig && (
            <div>
              <CardTitle style={{ marginBottom: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>Main service</CardTitle>
              <RackTable configoption={mainConfig} label="Rack U" />
            </div>
          )}
          {addonConfigs.length > 0 && (
            <div>
              <CardTitle style={{ marginBottom: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>Addons</CardTitle>
              <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                {addonConfigs.map(({ addon, config }) => (
                  <div key={addon.service_id}>
                    <CardHeader style={{ padding: 'var(--vn-space-2) var(--vn-space-3)', background: 'var(--vn-bg-muted)', borderTop: '1px solid var(--vn-border-subtle)' }}>
                      <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>
                        {addon.service_name}
                      </CardTitle>
                    </CardHeader>
                    <RackTable configoption={config} label="Rack U" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Standalone empty-state fallback for when a Colocation service exists but has
 * no rack-U configoption at all (e.g. "COLOCATION CUSTOMIZED" on staging).
 */
export function ColocationRackGridEmpty() {
  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Rack assignment</CardTitle>
      </CardHeader>
      <CardBody>
        <EmptyState
          icon={<Layers size={24} />}
          title="No rack assignment"
          description="This colocation service has no rack-U configoption recorded."
        />
      </CardBody>
    </Card>
  );
}
